/**
 * TeamSystem Impact — Consenso per un client AI (connettore MCP)
 *
 * Il backend valida la richiesta OAuth e rimanda qui. La persona e' quasi sempre
 * gia' dentro la piattaforma, quindi non vede nessun login: legge chi sta
 * chiedendo l'accesso, con quale utente sta autorizzando e cosa concede.
 *
 * I permessi mostrati sono quelli che otterra' DAVVERO: il backend li taglia al
 * ruolo, e questa schermata mostra il risultato del taglio, non la richiesta del
 * client.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, CircularProgress, Divider, Stack, Typography, Alert } from '@mui/material';
import {
  CheckCircleOutline as PermissionIcon,
  SmartToy as ClientIcon,
  Launch as DestinationIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { Button } from '@/components/ui/button';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { loginRequest } from '../../config/msalConfig';
import { logger } from '@/config/logger';
import { buildDenyUrl, readConsentParams, scopeLabelKey } from './consent';

interface ConsentInfo {
  client_name: string;
  granted_scopes: string[];
  denied_scopes: string[];
  /** Permessi richiesti che non esistono: errore del client, non del ruolo. */
  unknown_scopes?: string[];
  /** True quando i permessi richiesti non esistono: colpa del client, non del ruolo. */
  scope_unrecognised?: boolean;
  /** Host verificato a cui verra' consegnata l'autorizzazione. */
  redirect_host?: string;
}

export const OAuthConsentPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, utente, setAuth } = useAuthStore();
  const msal = useMsal();

  const params = useMemo(() => readConsentParams(location.search), [location.search]);

  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Non autenticato: prima si tenta l'accesso silenzioso. Se nel browser esiste
  // gia' una sessione Microsoft (tipico: la persona ha fatto login su Claude o su
  // Outlook), Microsoft ci autentica senza mostrare nulla e il collegamento
  // resta un clic solo. Se non e' possibile, si va al login normale conservando
  // l'indirizzo di partenza — parametri OAuth compresi — per riprendere da qui.
  useEffect(() => {
    if (isAuthenticated || !params) return;
    let annullato = false;

    const vaiAlLogin = () => {
      if (annullato) return;
      navigate('/login', { replace: true, state: { from: `${location.pathname}${location.search}` } });
    };

    (async () => {
      try {
        const risposta = await msal.instance.ssoSilent(loginRequest);
        if (annullato) return;
        const backend = await apiClient.post('/auth/login', { accessToken: risposta.accessToken });
        if (annullato) return;
        setAuth(backend.data.jwt, backend.data.utente, backend.data.refreshToken);
      } catch (err) {
        // Nessuna sessione Microsoft utilizzabile (o piu' account fra cui
        // scegliere): non e' un errore, si passa al login normale.
        logger.debug('Consenso: accesso silenzioso non disponibile', err);
        vaiAlLogin();
      }
    })();

    return () => {
      annullato = true;
    };
  }, [isAuthenticated, params, navigate, location.pathname, location.search, msal, setAuth]);

  useEffect(() => {
    if (!params || !isAuthenticated) {
      setLoading(false);
      return;
    }
    let annullato = false;
    (async () => {
      try {
        const query = new URLSearchParams({
          client_id: params.client_id,
          redirect_uri: params.redirect_uri,
        });
        if (params.scope) query.set('scope', params.scope);
        const res = await apiClient.get(`/oauth/consent-info?${query.toString()}`);
        if (!annullato) setInfo(res.data);
      } catch (err) {
        logger.error('Consenso: lettura dati client fallita', err);
        if (!annullato) setError(t('consent.errorLoading', 'Non riesco a leggere la richiesta di accesso.'));
      } finally {
        if (!annullato) setLoading(false);
      }
    })();
    return () => {
      annullato = true;
    };
  }, [params, isAuthenticated, t]);

  const handleAuthorize = useCallback(async () => {
    if (!params) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.post('/oauth/consent', params);
      window.location.assign(res.data.redirect_uri);
    } catch (err) {
      logger.error('Consenso: autorizzazione fallita', err);
      setError(t('consent.errorAuthorizing', 'Autorizzazione non riuscita. Riprova.'));
      setSubmitting(false);
    }
  }, [params, t]);

  const handleDeny = useCallback(() => {
    if (!params) return;
    // Si torna indietro SOLO verso una destinazione che il backend ha riconosciuto
    // come registrata per quel client. Senza questo confronto un link con un
    // redirect_uri http(s) qualsiasi trasformava il pulsante Annulla in un
    // trampolino di phishing partendo dal nostro dominio.
    const destinazione = buildDenyUrl(params.redirect_uri, params.state);
    let hostAtteso: string | null = null;
    try {
      hostAtteso = new URL(params.redirect_uri).host;
    } catch {
      hostAtteso = null;
    }
    if (!destinazione || !info?.redirect_host || info.redirect_host !== hostAtteso) {
      setError(t('consent.invalidRequest', 'Richiesta di accesso incompleta o non valida.'));
      return;
    }
    window.location.assign(destinazione);
  }, [params, info, t]);

  // Richiesta incompleta: non sappiamo nemmeno dove riportare l'utente, quindi
  // non c'e' nulla da autorizzare ne' da rifiutare.
  if (!params) {
    return (
      <CenteredCard>
        <Alert severity="error">
          {t('consent.invalidRequest', 'Richiesta di accesso incompleta o non valida.')}
        </Alert>
      </CenteredCard>
    );
  }

  if (loading || !isAuthenticated) {
    return (
      <CenteredCard>
        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
          <CircularProgress />
          <Typography color="text.secondary">{t('consent.loading', 'Un istante...')}</Typography>
        </Stack>
      </CenteredCard>
    );
  }

  const scopes = info?.granted_scopes ?? [];
  const nienteDaConcedere = scopes.length === 0;

  return (
    <CenteredCard>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ClientIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h6">{info?.client_name || t('consent.unknownClient', 'Client AI')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('consent.subtitle', 'vuole accedere a TeamSystem Impact')}
            </Typography>
          </Box>
        </Stack>

        <Divider />

        <Typography variant="body2" color="text.secondary">
          {t('consent.actingAs', 'Accederà come:')}{' '}
          <strong>
            {utente?.nome} {utente?.cognome}
          </strong>
        </Typography>

        {info?.redirect_host && (
          <Alert severity="info" icon={<DestinationIcon fontSize="small" />}>
            {t('consent.destination', 'L’autorizzazione verrà consegnata a:')}{' '}
            <strong>{info.redirect_host}</strong>
          </Alert>
        )}

        {nienteDaConcedere ? (
          <Alert severity="warning">
            {info?.scope_unrecognised
              ? t(
                  'consent.scopesUnrecognised',
                  'I permessi richiesti da questa applicazione non esistono in piattaforma: non è un problema del tuo ruolo. Segnalalo a chi l’ha configurata.',
                )
              : t(
                  'consent.nothingToGrant',
                  'Il tuo ruolo non consente nessuno dei permessi richiesti, quindi non c’è nulla da autorizzare.',
                )}
          </Alert>
        ) : (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('consent.willBeAbleTo', 'Potrà:')}
            </Typography>
            <Stack component="ul" spacing={1} sx={{ listStyle: 'none', pl: 0, m: 0 }}>
              {scopes.map((scope) => (
                <Stack key={scope} component="li" direction="row" spacing={1} alignItems="center">
                  <PermissionIcon fontSize="small" color="success" />
                  <Typography variant="body2">{t(scopeLabelKey(scope), scope)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {info && info.unknown_scopes && info.unknown_scopes.length > 0 && (
          <Alert severity="warning">
            {t(
              'consent.scopesUnrecognised',
              'I permessi richiesti da questa applicazione non esistono in piattaforma: non è un problema del tuo ruolo. Segnalalo a chi l’ha configurata.',
            )}
          </Alert>
        )}

        {info && info.denied_scopes.length > 0 && (
          <Alert severity="info">
            {t(
              'consent.someDenied',
              'Alcuni permessi richiesti non sono compresi, perché il tuo ruolo non li prevede.',
            )}
          </Alert>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {/*
          Nessun pulsante finche' il backend non ha validato client e destinazione:
          se consent-info ha rifiutato, non c'e' nessun posto legittimo dove
          mandare l'utente, nemmeno per annullare.
        */}
        {info && (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outline" onClick={handleDeny} disabled={submitting}>
              {t('consent.deny', 'Annulla')}
            </Button>
            {!nienteDaConcedere && (
              <Button onClick={handleAuthorize} disabled={submitting}>
                {submitting ? t('consent.authorizing', 'Autorizzo...') : t('consent.authorize', 'Autorizza')}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </CenteredCard>
  );
};

const CenteredCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      bgcolor: 'background.default',
    }}
  >
    <Card sx={{ maxWidth: 520, width: '100%' }}>
      <CardContent sx={{ p: 4 }}>{children}</CardContent>
    </Card>
  </Box>
);
